import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import AIPdfPanel from "../components/AIPdfPanel";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Download, RefreshCw, Share2, Check, FileDown, Image as ImageIcon, AlertCircle } from "lucide-react";
import EditorialEditor from "../components/EditorialEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MediaKit() {
  const { user } = useAuth();
  const [kit, setKit] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitErr, setLimitErr] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareSlug, setShareSlug] = useState("");
  const [hasOg, setHasOg] = useState(false);
  const [ogLoading, setOgLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    api.get("/ai/media-kit/latest").then(({data}) => {
      if (data?.data) setKit(data.data);
      if (data?.share_slug) {
        setShareSlug(data.share_slug);
        setShareUrl(`${BACKEND}/api/share/${data.share_slug}`);
      }
      if (data?.og_image_b64 || data?.og_image_path) setHasOg(true);
    });
    api.get("/profile").then(({data}) => setProfile(data));
  }, []);

  const generate = async () => {
    setLoading(true); setError(""); setLimitErr("");
    try {
      const { data } = await api.post("/ai/media-kit");
      setKit(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 402) setLimitErr(msg); else setError(msg);
    } finally { setLoading(false); }
  };

  const downloadHTML = () => {
    if (!kit) return;
    const html = renderMediaKitHTML(kit, profile, user);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(user?.full_name || "athlete").replace(/\s+/g,"_")}_media_kit.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    if (!kit) return;
    setPdfLoading(true);
    try {
      // Ensure we have a share slug so the print view can fetch the public kit data.
      let slug = shareSlug;
      if (!slug) {
        const { data } = await api.post("/media-kit/share");
        slug = data.slug;
        setShareSlug(slug);
        setShareUrl(`${BACKEND}/api/share/${slug}`);
      }
      // Open public kit in print mode — the viewer auto-triggers window.print() once loaded,
      // and the user can "Save as PDF" from the browser print dialog. Produces a clean,
      // multi-page editorial PDF that matches the public page exactly.
      const printWin = window.open(`/kit/${slug}?print=1`, "_blank");
      if (!printWin) {
        setError("Please allow popups for this site to export the PDF.");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const share = async () => {
    const { data } = await api.post("/media-kit/share");
    setShareSlug(data.slug);
    const url = `${BACKEND}/api/share/${data.slug}`;
    setShareUrl(url);
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const generateOg = async () => {
    setOgLoading(true); setError("");
    try {
      const { data } = await api.post("/media-kit/og-image");
      setShareSlug(data.slug);
      setShareUrl(`${BACKEND}/api/share/${data.slug}`);
      setHasOg(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setOgLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-10">
          <div>
            <div className="label-eyebrow">AI Generator</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="mediakit-title">Media Kit</h1>
            <p className="text-ink-muted mt-2 text-sm">Sponsor-grade copy generated from your profile.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={generate} disabled={loading} className="btn-volt inline-flex items-center gap-2" data-testid="mediakit-generate-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Generating…" : kit ? "Regenerate" : "Generate"}
            </button>
            <button onClick={downloadPDF} disabled={!kit || pdfLoading} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="mediakit-pdf-btn" title="Opens print view — use 'Save as PDF' in the print dialog">
              {pdfLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {pdfLoading ? "Opening…" : "Print / PDF"}
            </button>
            <button onClick={downloadHTML} disabled={!kit} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="mediakit-download-btn">
              <Download className="w-4 h-4" /> HTML
            </button>
            <button onClick={share} disabled={!kit} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="mediakit-share-btn">
              {copied ? <Check className="w-4 h-4 text-volt" /> : <Share2 className="w-4 h-4" />} {copied ? "Copied" : "Share link"}
            </button>
            <button onClick={generateOg} disabled={!kit || ogLoading} className="btn-ghost-line inline-flex items-center gap-2 disabled:opacity-30" data-testid="mediakit-og-btn">
              {ogLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} {hasOg ? "Regen OG card" : "AI OG card"}
            </button>
          </div>
        </div>

        {shareUrl && (
          <div className="surface p-4 mb-6 space-y-3" data-testid="share-url-display">
            <div className="flex flex-wrap items-center gap-3">
              <div className="label-eyebrow">Public share URL</div>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="text-volt text-sm font-mono break-all hover:underline">{shareUrl}</a>
            </div>
            {hasOg && shareSlug && (
              <div>
                <div className="label-eyebrow mb-2">AI-generated OG card (1200×630) — auto-rendered on social shares</div>
                <img src={`${BACKEND}/api/og/kit/${shareSlug}?t=${Date.now()}`} alt="OG card preview" className="w-full max-w-2xl border border-line" data-testid="og-preview" />
              </div>
            )}
          </div>
        )}

        {limitErr && (
          <div className="surface p-5 mb-6 border-volt/40 flex items-start gap-3" data-testid="mediakit-limit">
            <AlertCircle className="w-5 h-5 text-volt mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-heading text-base uppercase tracking-tight">Plan limit reached</div>
              <div className="text-sm text-ink-muted mt-1">{limitErr}</div>
              <Link to="/app/pricing" className="btn-volt inline-block mt-3" data-testid="mediakit-upgrade-link">Upgrade plan</Link>
            </div>
          </div>
        )}

        {error && <div className="surface p-4 border-[#FF3B30]/40 text-sm text-[#FF3B30] mb-6" data-testid="mediakit-error">{error}</div>}

        {!kit ? (
          <div className="surface p-12 text-center">
            <Sparkles className="w-8 h-8 text-volt mx-auto mb-4" />
            <h2 className="font-heading text-2xl uppercase">No media kit yet</h2>
            <p className="text-ink-muted mt-2 text-sm">Click generate to build your first sponsor-ready kit from your profile.</p>
          </div>
        ) : (
          <div data-testid="mediakit-preview" id="media-kit-preview" className="grid md:grid-cols-3 gap-3 bg-bg p-2">
            <div className="surface p-8 md:col-span-3">
              <div className="label-eyebrow mb-2">{user?.full_name || "Athlete"} — Athlete bio</div>
              <p className="text-lg leading-relaxed">{kit.athlete_bio || kit.professional_bio}</p>
            </div>
            <div className="surface p-6 md:col-span-2">
              <div className="label-eyebrow mb-2">Why partner</div>
              <p className="text-sm leading-relaxed">{kit.why_partner || kit.value_proposition}</p>
            </div>
            <div className="surface p-6">
              <div className="label-eyebrow mb-2">Audience</div>
              <p className="text-sm leading-relaxed text-ink-muted">{kit.audience_summary}</p>
            </div>
            {(kit.sponsorship_categories?.length > 0) && (
              <div className="surface p-6 md:col-span-3">
                <div className="label-eyebrow mb-3">Sponsorship categories that fit</div>
                <div className="flex flex-wrap gap-2">
                  {kit.sponsorship_categories.map((c, i) => <span key={i} className="px-3 py-1.5 border border-line text-xs uppercase tracking-widest text-volt">{c}</span>)}
                </div>
              </div>
            )}
            <div className="md:col-span-3 grid md:grid-cols-3 gap-3">
              {(kit.partnership_packages || kit.sponsorship_packages || []).map((p, i) => (
                <div key={i} className={`p-6 border ${i === 1 ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"}`}>
                  <div className="font-heading text-2xl uppercase">{p.name}</div>
                  <div className="font-mono text-xs text-volt mt-1">{p.price_range}</div>
                  <ul className="mt-4 space-y-2 text-sm">
                    {(p.deliverables || []).map((d, j) => <li key={j} className="text-ink-muted">— {d}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="surface p-6 md:col-span-3">
              <div className="label-eyebrow mb-3">Campaign ideas</div>
              <ul className="grid md:grid-cols-2 gap-2">
                {(kit.campaign_ideas || kit.collaboration_ideas || []).map((c, i) => (
                  <li key={i} className="border border-line p-3 text-sm">{c}</li>
                ))}
              </ul>
            </div>
            {kit.closing_paragraph && (
              <div className="surface p-8 md:col-span-3">
                <div className="label-eyebrow mb-2">Closing</div>
                <p className="text-base leading-relaxed italic text-ink-muted">{kit.closing_paragraph}</p>
              </div>
            )}
          </div>
        )}

        <CommercialNotice />

        {user?.id && <AIPdfPanel user={user} />}
        {user?.id && <EditorialEditor userId={user.id} />}
      </div>
    </DashboardLayout>
  );
}

function renderMediaKitHTML(kit, profile, user) {
  const bio = kit.athlete_bio || kit.professional_bio || "";
  const why = kit.why_partner || kit.value_proposition || "";
  const cats = kit.sponsorship_categories || [];
  const packages = (kit.partnership_packages || kit.sponsorship_packages || []).map(p =>
    `<div class="pkg"><h3>${esc(p.name)}</h3><div class="price">${esc(p.price_range)}</div><ul>${(p.deliverables||[]).map(d=>`<li>${esc(d)}</li>`).join("")}</ul></div>`
  ).join("");
  const ideas = (kit.campaign_ideas || kit.collaboration_ideas || []).map(i => `<li>${esc(i)}</li>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(user?.full_name || "Media Kit")} — Media Kit</title>
<style>
  body{background:#0A0A0A;color:#fff;font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:48px}
  h1,h2,h3{font-family:'Oswald','Arial Black',sans-serif;text-transform:uppercase;letter-spacing:-0.02em;margin:0}
  h1{font-size:48px;color:#fff} h2{font-size:24px;margin-top:32px}
  .eyebrow{color:#A1A1AA;font-size:11px;letter-spacing:.2em;text-transform:uppercase}
  .volt{color:#E2FF31}
  .card{border:1px solid #2A2A2A;padding:24px;margin:16px 0;background:#141414}
  .pkg{border:1px solid #2A2A2A;padding:20px;background:#141414}
  .pkg h3{color:#E2FF31;font-size:20px}
  .pkg .price{color:#A1A1AA;font-size:12px;margin:8px 0;font-family:monospace}
  .pkg ul{padding-left:18px;color:#A1A1AA;font-size:14px}
  .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
  ul{line-height:1.7} p{line-height:1.7;color:#fff}
  .pill{display:inline-block;border:1px solid #2A2A2A;padding:6px 12px;margin:4px;font-size:11px;text-transform:uppercase;letter-spacing:.2em;color:#E2FF31}
</style></head><body>
<div class="eyebrow">PITCHD Media Kit</div>
<h1>${esc(user?.full_name || profile?.full_name || "Athlete")}</h1>
<div class="eyebrow" style="margin-top:8px">${esc(profile?.sport || "")} · ${esc(profile?.level || "")} · ${esc(profile?.location || "")}</div>
<div class="card"><div class="eyebrow">Athlete bio</div><p>${esc(bio)}</p></div>
<div class="card"><div class="eyebrow">Why partner</div><p>${esc(why)}</p></div>
<div class="card"><div class="eyebrow">Audience</div><p>${esc(kit.audience_summary || "")}</p></div>
${cats.length ? `<div class="card"><div class="eyebrow">Sponsorship categories</div><div>${cats.map(c=>`<span class="pill">${esc(c)}</span>`).join("")}</div></div>` : ""}
<h2>Partnership Packages</h2>
<div class="grid">${packages}</div>
<h2>Campaign Ideas</h2>
<div class="card"><ul>${ideas}</ul></div>
${kit.closing_paragraph ? `<div class="card"><div class="eyebrow">Closing</div><p>${esc(kit.closing_paragraph)}</p></div>` : ""}
</body></html>`;
}
function esc(s){return String(s||"").replace(/[&<>"']/g, c=>({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"})[c]);}
